import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Meta, Schema, Breadcrumbs, buildTitle, buildDescription } from "../components/SEO";
import { findCity, cities, isPriorityCity } from "../data/cities";
import { services, serviceCategories } from "../data/services";
import { usePublicProjects } from "../hooks/useProjects";
import { ReviewsSection, CtaStrip } from "../components/Sections";
import { QuoteForm } from "../components/QuoteForm";
import { SmartImage } from "../components/SmartImage";

export default function LocationPage() {
  const params = useParams();
  // Support both /locaties/:city and /werkgebied/:slug (where slug = "aannemer-{city}")
  let cityKey = params.city;
  if (!cityKey && params.slug) {
    cityKey = params.slug.startsWith("aannemer-") ? params.slug.slice("aannemer-".length) : params.slug;
  }
  const place = findCity(cityKey);
  const { allProjects } = usePublicProjects();
  if (!place) return <Navigate to="/locaties" replace />;

  const localProjects = allProjects.filter((p) => p.city === place.name);

  return (
    <>
      <Meta
        title={buildTitle(`Aannemer ${place.name} voor verbouwing en renovatie`)}
        description={buildDescription(`Aannemer in ${place.name} voor renovatie, badkamer, zolder en aanbouw. Lokale ervaring, vaste prijs en een vast uitvoeringsteam. Binnen één werkdag contact.`)}
        noindex={!isPriorityCity(place.slug)}
        canonicalPath={`/locaties/${place.slug}`}
      />
      <Schema data={{
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: `Koning Aannemersbedrijf — ${place.name}`,
        image: "https://koningaannemers.nl/brand/logo.svg",
        url: `https://koningaannemers.nl/locaties/${place.slug}`,
        telephone: "06 - 35 65 06 10",
        priceRange: "€€€",
        address: {
          "@type": "PostalAddress",
          addressLocality: place.name,
          addressRegion: "Zuid-Holland",
          addressCountry: "NL",
        },
        areaServed: place.neighborhoods.map((n) => ({ "@type": "Place", name: `${n}, ${place.name}` })),
      }} />
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Werkgebied", url: "/werkgebied" },
        { name: place.name, url: `/locaties/${place.slug}` },
      ]} />

      <section className="relative min-h-[65vh] flex items-end overflow-hidden grain" data-testid="location-hero">
        <SmartImage src={place.image} alt={`Aannemer ${place.name}`} sizes="100vw" eager className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.28em] uppercase text-koning-gold mb-4">
              <Link to="/locaties" className="hover:underline">Werkgebied</Link> · {place.name}
            </div>
            <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Aannemer <span className="italic text-koning-gold">{place.name}</span>
            </h1>
            <p className="mt-6 text-koning-text-2/90 max-w-xl leading-relaxed">{place.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/offerte" className="btn-gold" data-testid="location-cta-offerte">Vraag offerte aan <ArrowUpRight size={16} /></Link>
              <Link to="/projecten" className="btn-outline">Bekijk projecten</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-20 md:py-28" data-testid="location-intro">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1.2fr_1fr] gap-14">
          <div>
            <div className="eyebrow mb-4">Lokaal werkzaam</div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl">
              Vertrouwd in heel <span className="italic text-koning-gold">{place.name}</span>
            </h2>
            <p className="mt-6 text-koning-muted leading-relaxed max-w-2xl">
              Koning Aannemersbedrijf realiseert verbouwingen en renovaties in alle wijken van {place.name}. Van karakteristieke woningen tot strakke moderne appartementen — wij hebben de ervaring om elk type pand naar een hoger niveau te tillen.
            </p>
            {place.localContext && (
              <p className="mt-4 text-koning-muted leading-relaxed max-w-2xl">{place.localContext}</p>
            )}

            <div className="mt-8">
              <div className="text-xs tracking-[0.24em] uppercase text-koning-muted mb-3">Wij werken in onder andere:</div>
              <div className="flex flex-wrap gap-2">
                {place.neighborhoods.map((n) => (
                  <span key={n} className="flex items-center gap-2 px-4 py-2 border border-koning-border text-sm text-koning-text">
                    <MapPin size={12} className="text-koning-gold" /> {n}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="border border-koning-border p-5">
                <div className="font-display text-koning-gold text-2xl">Vast team</div>
                <div className="text-xs tracking-[0.22em] uppercase text-koning-muted mt-2">Vast uitvoeringsteam</div>
              </div>
              <div className="border border-koning-border p-5">
                <div className="font-display text-koning-gold text-2xl">VCA</div>
                <div className="text-xs tracking-[0.22em] uppercase text-koning-muted mt-2">Gecertificeerde medewerkers</div>
              </div>
              <div className="border border-koning-border p-5">
                <div className="font-display text-koning-gold text-3xl">15+</div>
                <div className="text-xs tracking-[0.22em] uppercase text-koning-muted mt-2">Jaar praktijkervaring</div>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            <QuoteForm initialService="" compact />
          </div>
        </div>
      </section>

      {/* Services in this city */}
      <section className="py-20 md:py-28 bg-koning-surface border-y border-koning-border" data-testid="location-services">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-10">
            <div className="eyebrow mb-3">Onze diensten in {place.name}</div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">Specialist voor elk vakgebied</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {serviceCategories.map((c) => (
              <div key={c.slug} className="border-t border-koning-gold pt-5">
                <div className="font-display text-white text-lg">{c.title}</div>
                <ul className="mt-3 space-y-2">
                  {services.filter((s) => s.category === c.slug).map((s) => (
                    <li key={s.slug}>
                      <Link to={`/diensten/${s.slug}/${place.slug}`} className="text-sm text-koning-muted hover:text-white transition-colors" data-testid={`location-service-${s.slug}`}>
                        {s.title} {place.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local projects */}
      {localProjects.length > 0 && (
        <section className="py-20 md:py-28" data-testid="location-projects">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="eyebrow mb-3">Projecten in {place.name}</div>
                <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">Recente realisaties</h2>
              </div>
              <Link to="/projecten" className="btn-outline">Alle projecten <ArrowUpRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {localProjects.map((p) => (
                <Link key={p.slug} to={`/projecten/${p.slug}`} className="group img-zoom block border border-koning-border">
                  <div className="relative aspect-[4/5]">
                    <SmartImage src={p.cover} alt={p.title} aspect="4/5" sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-koning-bg/95 via-koning-bg/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase">{p.category}</div>
                      <h3 className="font-display text-white text-xl mt-2">{p.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ReviewsSection limit={3} title="Wat klanten zeggen" />

      {/* Other cities */}
      <section className="py-16 border-t border-koning-border" data-testid="location-other-cities">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs tracking-[0.24em] uppercase text-koning-muted mb-4">Andere steden</div>
          <div className="flex flex-wrap gap-2">
            {cities.filter((c) => c.slug !== place.slug).map((c) => (
              <Link key={c.slug} to={`/locaties/${c.slug}`} className="px-4 py-2 border border-koning-border text-sm text-koning-muted hover:text-koning-gold hover:border-koning-gold transition-colors">
                Aannemer {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaStrip title={`Klaar voor uw project in ${place.name}?`} subtitle="Binnen één werkdag persoonlijk contact." />
    </>
  );
}
